import { forwardRef } from 'react';
import { Typography } from '../Typography';
import { defaultTheme } from '@automata/mui-theme';
import Stack from '@mui/material/Stack';
import { SxProps, Theme } from '@mui/material/styles';
import MuiTextField, {
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField';
import { v4 as uuidv4 } from 'uuid';
import FormControl from '@mui/material/FormControl';
import { InfoIconPopover, type InfoIconPopoverProps } from '../InfoIconPopover';

export type ExtraConfig = {
  inputHelpText?: string | null;
  labelStyles?: SxProps<Theme>;
};

export type TextFieldProps = Omit<MuiTextFieldProps, 'size' | 'popover'> &
  Partial<InfoIconPopoverProps> &
  ExtraConfig;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function Forwarded(
    {
      id = `textField-${uuidv4()}`,
      label,
      labelStyles,
      infoPopover,
      infoPopoverMaxWidth,
      infoPopoverAnchorOrigin,
      infoPopoverTransformOrigin,
      inputHelpText,
      ...props
    },
    ref
  ) {
    return (
      <Stack width={props.fullWidth ? '100%' : 'inherit'}>
        {(label || infoPopover) && (
          <Typography
            alignItems="center"
            color={defaultTheme.palette.text.primary}
            component="label"
            display="flex"
            gap={0.5}
            htmlFor={id}
            pb={0.5}
            pt={1}
            sx={labelStyles}
            variant="overline"
          >
            {label}
            {infoPopover ? (
              <InfoIconPopover
                infoPopover={infoPopover}
                infoPopoverMaxWidth={infoPopoverMaxWidth}
                infoPopoverAnchorOrigin={infoPopoverAnchorOrigin}
                infoPopoverTransformOrigin={infoPopoverTransformOrigin}
              />
            ) : null}
          </Typography>
        )}
        <FormControl>
          <MuiTextField
            id={id}
            {...props}
            ref={ref}
            size="small"
            label={inputHelpText}
          />
        </FormControl>
      </Stack>
    );
  }
);
